import type { ReactNode } from 'react';
import { Card, Icon } from '../primitives';
import type { IconName } from '../primitives';
import { AdvanceTrigger } from './AdvanceTrigger';
import './semantic.groupC.css';

/**
 * Foundation semantic: StartGate（2026-06-12 R1: 体験文法の統一）
 *
 * 捜査文法（mood=tension 主想定）の「開始ゲート」。ゲーム領域を覆う
 * オーバーレイ（--mg-overlay）＋中央 Card（アイコン→タイトル→説明→開始ボタン）で、
 * 19 テンプレでバラバラだった「開始 5 流儀」を 1 つに統一する。
 *
 * Visual Grammar:
 *  - 開始＝進行なので、開始ボタンは AdvanceTrigger（進行=右・ChevronRight 既定）。
 *  - 出現はフェードイン（--mg-dur-base / --mg-ease-out。CSS 側）。
 *
 * 表示制御:
 *  - 表示/非表示は呼び出し側が制御する（このコンポーネントは open prop を持たない。
 *   出すなら描画し、閉じるなら描画しない）。`hostMeta.ui?.showTutorial !== false` の
 *   とき表示する想定。
 *  - 親ステージに position（relative 等）が必要（JudgementBurst と同じ前提）。
 *
 * 規約:
 *  - hex 直書き禁止。色は mood トークン経由（semantic.groupC.css 側）。
 *  - アイコンは Icon primitive 経由。icon に string を渡すと lucide 名として解釈する
 *   （実在しない名前は Icon が null を返すため安全に何も出ない）。
 */
export interface StartGateProps {
  /** ゲームタイトル（必須）。中央 Card に --mg-text-xl / bold で表示 */
  title: string;
  /** ルール説明（任意）。string でも JSX でも可。--mg-text-sm / leading-normal */
  instruction?: ReactNode;
  /** 開始ボタンのラベル。未指定時は「始める」 */
  startLabel?: string;
  /** タイトル上の丸バッジに出すアイコン（任意）。string は lucide の Icon name として解釈 */
  icon?: ReactNode | IconName;
  /** 開始ボタン押下時のコールバック */
  onStart: () => void;
  /** 追加 className（z-index 調整等） */
  className?: string;
}

export function StartGate({
  title,
  instruction,
  startLabel = '始める',
  icon,
  onStart,
  className,
}: StartGateProps) {
  // string は lucide アイコン名として Icon primitive に解決させる
  // （typeof で ReactNode と区別する。未知の名前は Icon が null を返す）。
  const renderedIcon =
    typeof icon === 'string' ? <Icon name={icon as IconName} size={28} /> : icon;

  const classes = ['mg-startgate', className].filter(Boolean).join(' ');

  return (
    <div className={classes} role="dialog" aria-modal="true" aria-label={title}>
      <Card className="mg-startgate__panel">
        {renderedIcon ? (
          <div className="mg-startgate__icon" aria-hidden>
            {renderedIcon}
          </div>
        ) : null}
        <h2 className="mg-startgate__title">{title}</h2>
        {instruction ? (
          <div className="mg-startgate__instruction">{instruction}</div>
        ) : null}
        <div className="mg-startgate__actions">
          {/* 開始ボタンは AdvanceTrigger なので、開始音（select）は AdvanceTrigger 側で
              自動的に鳴る。ここで二重に鳴らさないこと（R2 Wave2B の二重発火防止）。 */}
          <AdvanceTrigger label={startLabel} onAdvance={onStart} />
        </div>
      </Card>
    </div>
  );
}
